/* eslint-disable @typescript-eslint/unbound-method */

import type {
  TrackerSdkOptions,
  TrackProperties,
} from '@brainfish-ai/tracker-sdk';
import { TrackerSdk } from '@brainfish-ai/tracker-sdk';
import Agent from './record';
import SessionManager from './session/sessionManager';

export type * from '@brainfish-ai/tracker-sdk';

export type TrackerOptions = TrackerSdkOptions & {
  trackOutgoingLinks?: boolean;
  trackScreenViews?: boolean;
  trackAttributes?: boolean;
  trackHashChanges?: boolean;
  enableRecording?: boolean;
  recordingBlocklist?: string[];
  _allowLocalhostRecording?: boolean;
};

function toCamelCase(str: string) {
  return str.replace(/([-_][a-z])/gi, ($1) =>
    $1.toUpperCase().replace('-', '').replace('_', ''),
  );
}

export const VERSION = import.meta.env.PACKAGE_VERSION as string;

export class Tracker extends TrackerSdk {
  private lastPath = '';
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly agent: Agent;
  private readonly sessionManager = SessionManager.getInstance();
  private readonly recordingBlocklist: string[] = [];
  private isRecordingActive = false;

  constructor(public options: TrackerOptions) {
    super({
      sdk: 'web',
      sdkVersion: VERSION,
      ...options,
    });

    this.agent = new Agent();
    this.recordingBlocklist = options.recordingBlocklist || [];

    if (!this.isServer()) {
      this.setGlobalProperties({
        __referrer: document.referrer,
      });

      // Check if tracking is disabled - if so, skip privacy-sensitive features
      const trackingDisabled = this.isTrackingDisabled();

      // track outgoing links (privacy-sensitive)
      if (this.options.trackOutgoingLinks && !trackingDisabled) {
        this.trackOutgoingLinks();
      }

      // track screen views
      if (this.options.trackScreenViews) {
        this.trackScreenViews();
      }

      // track attributes on buttons and links (privacy-sensitive)
      if (this.options.trackAttributes && !trackingDisabled) {
        this.trackAttributes();
      }

      // start recording events if recording is enabled (privacy-sensitive)
      if (this.options.enableRecording && !trackingDisabled) {
        this.startRecording();
        this.isRecordingActive = true;
        console.log('Brainfish has started ambient learning');
      }
    }
  }

  private debounce(func: () => void, delay: number) {
    this.debounceTimer && clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(func, delay);
  }

  private isServer() {
    return typeof document === 'undefined';
  }

  public trackOutgoingLinks() {
    if (this.isServer()) {
      return;
    }

    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a');
      if (link && target) {
        const href = link.getAttribute('href');
        if (href?.startsWith('http')) {
          super.track('link_out', {
            href,
            text:
              link.innerText ||
              link.getAttribute('title') ||
              target.getAttribute('alt') ||
              target.getAttribute('title'),
          });
        }
      }
    });
  }

  public trackScreenViews() {
    if (this.isServer()) {
      return;
    }

    this.screenView();

    const oldPushState = history.pushState;
    history.pushState = function pushState(...args) {
      const ret = oldPushState.apply(this, args);
      window.dispatchEvent(new Event('pushstate'));
      window.dispatchEvent(new Event('locationchange'));
      return ret;
    };

    const oldReplaceState = history.replaceState;
    history.replaceState = function replaceState(...args) {
      const ret = oldReplaceState.apply(this, args);
      window.dispatchEvent(new Event('replacestate'));
      window.dispatchEvent(new Event('locationchange'));
      return ret;
    };

    window.addEventListener('popstate', function () {
      window.dispatchEvent(new Event('locationchange'));
    });

    // Use a longer debounce to allow for render stability check
    const eventHandler = () => this.debounce(() => this.screenView(), 100);

    if (this.options.trackHashChanges) {
      window.addEventListener('hashchange', eventHandler);
    } else {
      window.addEventListener('locationchange', eventHandler);
    }
  }

  public trackAttributes() {
    if (this.isServer()) {
      return;
    }

    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const btn = target.closest('button');
      const anchor = target.closest('a');
      const element = btn?.getAttribute('data-track')
        ? btn
        : anchor?.getAttribute('data-track')
        ? anchor
        : null;
      if (element) {
        const properties: Record<string, unknown> = {};
        for (const attr of element.attributes) {
          if (attr.name.startsWith('data-') && attr.name !== 'data-track') {
            properties[toCamelCase(attr.name.replace(/^data-/, ''))] =
              attr.value;
          }
        }
        const name = element.getAttribute('data-track');
        if (name) {
          super.track(name, properties);
        }
      }
    });
  }

  public startRecording() {
    if (this.isServer()) {
      return;
    }

    // start recording events
    this.agent.start({
      sendEvents: this.recordEventsToServer.bind(this),
      sendScreenViewEvent: this.sendScreenViewEvent.bind(this),
      recordingBlocklist: this.recordingBlocklist,
      _allowLocalhostRecording: this.options._allowLocalhostRecording,
    });
  }

  async screenView(properties?: TrackProperties): Promise<void>;
  async screenView(path: string, properties?: TrackProperties): Promise<void>;
  async screenView(
    pathOrProperties?: string | TrackProperties,
    propertiesOrUndefined?: TrackProperties,
  ): Promise<void> {
    if (this.isServer()) {
      return;
    }

    let path: string;
    let properties: TrackProperties | undefined;

    if (typeof pathOrProperties === 'string') {
      path = pathOrProperties;
      properties = propertiesOrUndefined;
    } else {
      path = window.location.href;
      properties = pathOrProperties;
    }

    if (this.lastPath === path) {
      return;
    }

    this.lastPath = path;

    const trackProps: any = {
      ...(properties ?? {}),
      __path: path,
      __title: document.title,
    };

    this.sendScreenViewEvent(trackProps);
  }

  /**
   * Handles recording events to the server and manages session state transitions.
   * @param events Array of events to be recorded
   * @returns Promise that resolves when events are recorded and session is managed
   */

  private async recordEventsToServer(events: unknown[]): Promise<void> {
    const sessionId = await this.record(events);

    this.sessionManager.updateSession(sessionId, {
      takeFullSnapshot: () => {
        this.agent.takeFullSnapshot();
      },
    });
  }

  private sendScreenViewEvent(props: any) {
    super.track('screen_view', props);
    this.sessionManager.updateLastScreenViewEventTime();
  }

  /**
   * Disables privacy-sensitive tracking features and stops active recording.
   */
  override disableTracking() {
    super.disableTracking();

    // Stop active recording if it's running
    if (this.isRecordingActive) {
      if (this.agent.recordingManager.recorder.stop) {
        this.agent.recordingManager.recorder.stop();
      }
      this.isRecordingActive = false;
      console.log('Brainfish has stopped ambient learning');
    }
  }

  /**
   * Re-enables privacy-sensitive tracking features.
   * Note: Full effect requires a page reload to restart recording and event listeners.
   */
  override enableTracking() {
    super.enableTracking();
  }
}
